import React from 'react';
import { motion } from 'framer-motion';
import { X, Target, Clock, Activity } from 'lucide-react';
import { CustomWorkout } from '../../../types/exercises';

interface WorkoutSettingsProps {
  onClose: () => void;
  workout: Partial<CustomWorkout>;
  onUpdate: (settings: { name: string; description?: string }) => void;
}

export function WorkoutSettings({ onClose, workout, onUpdate }: WorkoutSettingsProps) {
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Workout Settings</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Workout Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Duration</div>
              <div className="font-medium flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {workout.estimatedDuration || 0} min
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Target Strain</div>
              <div className="font-medium flex items-center gap-1">
                <Target className="w-4 h-4" />
                {workout.targetStrain?.toFixed(1) || '0.0'}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Type</div>
              <div className="font-medium flex items-center gap-1">
                <Activity className="w-4 h-4" />
                {workout.type || 'Mixed'}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Difficulty</div>
              <div className="font-medium capitalize">
                {workout.difficulty || 'Intermediate'}
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Tags</label>
            <div className="flex flex-wrap gap-2">
              {['Strength', 'Upper Body', 'Compound', 'High Volume'].map((tag) => (
                <div
                  key={tag}
                  className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs"
                >
                  {tag}
                </div>
              ))}
              <button className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs hover:bg-muted/70 transition-colors">
                + Add Tag
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full theme-button py-2 rounded-lg"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
}